import * as Sentry from "@sentry/react";

import {
  addBreadcrumb,
  captureError,
  captureMessage,
} from "../../utils/sentry";

/**
 * Development component to test Sentry integration
 * Only shows in development mode
 */
export function SentryTestButton() {
  // Only show in development and preview
  if (
    import.meta.env.VITE_NODE_ENV !== "development" &&
    import.meta.env.VITE_NODE_ENV !== "preview"
  ) {
    return null;
  }

  const testSentryDefaultError = () => {
    try {
      throw new Error("This is your first error!");
    } catch (error) {
      // Manually capture the error with Sentry
      captureError(error as Error, {
        component: "ErrorButton",
        testType: "manual-error",
      });
      console.log("Error captured and sent to Sentry:", error);
    }
  };

  const testError = () => {
    addBreadcrumb("User clicked test error button", "ui");
    const error = new Error("Test error from Sentry integration");

    // Use both our custom captureError and direct Sentry call
    captureError(error, {
      testType: "manual",
      component: "SentryTestButton",
    });

    // Also directly call Sentry to ensure it's working
    Sentry.captureException(error);

    console.log("Test error sent to Sentry via both methods!");
    alert("Test error sent to Sentry! Check your Sentry dashboard.");
  };

  const testMessage = () => {
    addBreadcrumb("User clicked test message button", "ui");
    captureMessage("Test message from Sentry integration", "info", {
      testType: "manual",
      component: "SentryTestButton",
    });
    alert("Test message sent to Sentry! Check your Sentry dashboard.");
  };

  const testUnhandledException = () => {
    addBreadcrumb("User clicked test unhandled exception button", "ui");
    // This will trigger the ErrorBoundary
    setTimeout(() => {
      throw new Error("Test unhandled exception for ErrorBoundary");
    }, 100);
  };

  const testNetworkError = () => {
    addBreadcrumb("User clicked test network error button", "ui");
    // Simulate a network error
    fetch("/api/nonexistent-endpoint").catch((error) => {
      captureError(error, {
        testType: "network",
        component: "SentryTestButton",
      });
      alert("Test network error sent to Sentry! Check your Sentry dashboard.");
    });
  };

  const testSentryConnection = () => {
    console.log("Testing Sentry connection...");

    try {
      // Test basic Sentry functionality
      Sentry.addBreadcrumb({
        message: "Test connection clicked",
        level: "info",
        category: "ui",
      });

      Sentry.captureMessage(
        "Sentry connection test - this should appear in your dashboard",
        "info",
      );
      console.log("✅ Sentry connection test completed successfully");
      alert("Sentry connection test sent! Check console and dashboard.");
    } catch (error) {
      console.error("❌ Sentry connection test failed:", error);
      alert("Sentry connection test failed! Check console for details.");
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 bg-gray-800 text-white p-4 rounded-lg shadow-lg">
      <h3 className="text-sm font-semibold mb-2">Sentry Test (Dev Only)</h3>
      <div className="space-y-2">
        <button
          onClick={testSentryConnection}
          className="block w-full text-xs bg-green-600 hover:bg-green-700 px-2 py-1 rounded"
        >
          Test Connection
        </button>
        <button
          onClick={testSentryDefaultError}
          className="block w-full text-xs bg-yellow-600 hover:bg-yellow-700 px-2 py-1 rounded"
        >
          Test Error (default)
        </button>
        <button
          onClick={testError}
          className="block w-full text-xs bg-red-600 hover:bg-red-700 px-2 py-1 rounded"
        >
          Test Error (with breadcrumb)
        </button>
        <button
          onClick={testMessage}
          className="block w-full text-xs bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded"
        >
          Test Message
        </button>
        <button
          onClick={testUnhandledException}
          className="block w-full text-xs bg-orange-600 hover:bg-orange-700 px-2 py-1 rounded"
        >
          Test Exception
        </button>
        <button
          onClick={testNetworkError}
          className="block w-full text-xs bg-purple-600 hover:bg-purple-700 px-2 py-1 rounded"
        >
          Test Network Error
        </button>
      </div>
    </div>
  );
}
